import { useEffect, useState } from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import { columnSettings } from "./report-template-setting";

// Helper to group data by jenis_minyak_id
const groupByJenisMinyak = (data) => {
    const map = {};

    data.forEach((item) => {
        // Skip items with no jenis_minyak
        if (!item.data_jenis_minyak) return;

        const key = item.jenis_minyak_id;
        if (!map[key]) {
            map[key] = {
                list_data: [],
                group_data: item.data_jenis_minyak,
            };
        }
        map[key].list_data.push(item);
    });

    return map;
};

const MobileList = ({ listData = [], loading }) => {
    const [groupMap, setGroupMap] = useState({});
    const [groupNames, setGroupNames] = useState([]);
    const [summaryRows, setSummaryRows] = useState([]);

    useEffect(() => {
        if (!listData || listData.length === 0) return;

        // Group data by jenis_minyak_id
        const grouped = groupByJenisMinyak(listData);
        const names = Object.keys(grouped);
        setGroupMap(grouped);
        setGroupNames(names);

        // Build summary rows
        let totalCount = 0;
        let totalJumlah = 0;
        let totalHarga = 0;
        const rows = [];

        names.forEach((name, idx) => {
            const group = grouped[name];
            const groupData = group.group_data;

            const total_liter = group.list_data.reduce((sum, r) => sum + (r.jumlah || 0), 0);
            const total_harga = group.list_data.reduce((sum, r) => sum + (r.total_harga || 0), 0);

            const row = {
                no: idx + 1,
                nama: groupData?.nama || "-",
                count: group.list_data.length,
                total_liter,
                total_harga,
            };

            totalCount += row.count;
            totalJumlah += row.total_liter;
            totalHarga += row.total_harga;
            rows.push(row);
        });

        // Calculate percentages
        rows.forEach((r) => {
            r.persen = totalHarga ? ((r.total_harga / totalHarga) * 100).toFixed(2) : 0;
        });

        setSummaryRows(rows);
    }, [listData]);

    const formatValue = (col, value) => {
        if (col.type === "number" && value !== null && value !== undefined) {
            return value.toLocaleString("id-ID");
        } else
            if (col.type === "date" && value) {
                return new Date(value).toLocaleDateString("id-ID", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                });
            } else if (value === null || value === undefined) {
                return "-";
            } else if (typeof value === "object") {
                if (value?.nama) return value.nama;
                return (value.nomor_polisi);
            }
        return value;
    };

    return (
        <div className="flex flex-col gap-4 w-full p-4">
            {loading && (
                <div className="flex justify-center">
                    <ProgressSpinner />
                </div>
            )}

            {!loading && summaryRows.length > 0 && (
                <div className="bg-zinc-50 rounded-xl shadow-sm border border-zinc-200 overflow-hidden">
                    <div className="bg-indigo-100 px-4 py-2 font-semibold text-indigo-800 border-b border-zinc-200">
                        SUMMARY LAPORAN PER JENIS MINYAK
                    </div>
                    <div className="px-4 py-2 flex flex-col gap-2 bg-white">
                        {summaryRows.map((row) => (
                            <div
                                key={`summary-${row.no}`}
                                className="col-start-start border-b border-zinc-200 pb-1 last:border-b-0"
                            >
                                <span className="font-bold">{row.nama}</span>
                                <span>
                                    {row.count.toLocaleString("id-ID")} Kendaraan
                                </span>
                                <span>
                                    {row.total_liter.toLocaleString("id-ID")} Ltr
                                </span>
                                <span>
                                    Rp {row.total_harga.toLocaleString("id-ID")} ({row.persen}%)
                                </span>
                            </div>
                        ))}
                    </div>
                    <div>
                        <div className="col-start-start px-4 py-2 bg-indigo-50 text-indigo-800 font-semibold">
                            TOTAL:
                            <span>{summaryRows.reduce((sum, r) => sum + r.count, 0).toLocaleString("id-ID")} kendaraan</span>
                            <span>{summaryRows.reduce((sum, r) => sum + r.total_liter, 0).toLocaleString("id-ID")} Ltr</span>
                            <span>Rp. {summaryRows.reduce((sum, r) => sum + r.total_harga, 0).toLocaleString("id-ID")} (100%)</span>
                        </div>
                    </div>
                </div>
            )}

            {
                !loading &&
                groupNames.map((key) => {
                    const group = groupMap[key];
                    const groupRows = group.list_data;
                    const groupData = group.group_data;

                    const total_liter = groupRows.reduce((sum, r) => sum + (r.jumlah || 0), 0);
                    const total_harga = groupRows.reduce((sum, r) => sum + (r.total_harga || 0), 0);

                    return (
                        <div
                            key={key}
                            className="bg-zinc-50 rounded-xl shadow-sm border border-zinc-200 overflow-hidden"
                        >
                            {/* Group Header */}
                            <div className="bg-indigo-100 px-4 py-2 font-semibold text-indigo-800 border-b border-zinc-200">
                                {groupData?.nama || "-"}
                            </div>

                            {/* Detail Rows */}
                            <div className="px-4 py-2 flex flex-col gap-2 bg-white">
                                {groupRows.map((row, idx) => (
                                    <div
                                        key={row.id ?? idx}
                                        className="flex flex-col border-b border-zinc-200 pb-2 last:border-b-0"
                                    >
                                        {columnSettings
                                            .filter((col) => col.field !== "no") // skip "no" column
                                            .map((col) => (
                                                <div key={col.field} className="flex justify-between mb-1">
                                                    <span className="text-slate-600 font-bold">{col.name}:</span>
                                                    <span className="text-slate-800">
                                                        {formatValue(col, row[col.field] ?? row[`data_${col.field}`])}
                                                    </span>
                                                </div>
                                            ))}
                                    </div>
                                ))}
                            </div>

                            {/* Group Footer */}
                            <div className="px-4 py-2 bg-indigo-50 text-indigo-800 font-semibold text-right">
                                <div className="row-between-start">
                                    <span>Total Kendaraan: {groupRows.length?.toLocaleString("id-ID")} </span>
                                    <span>Total Liter: {total_liter?.toLocaleString("id-ID")}</span>
                                </div>
                                <div className="row-center-start">
                                    <span>Total Harga: Rp{" "}{total_harga?.toLocaleString("id-ID")}</span>
                                </div>
                            </div>
                        </div>
                    );
                })
            }
        </div >
    );
};

export default MobileList;
